/**
 * 「初日から稼げる」の画面（/money/play-earnings・pages/money/PlayEarnPage.tsx・2026-10-26 money/earn-build）の英語の文言。en.ts が `...enPlayEarn` で取り込む。
 * 絵の元: docs/teams/money/earn-launch/dashboard-earnings-en.png（企画書 §5）。
 *
 * 言い回しの決め事:
 *   - Growth の決まり（docs/growth/campaign/2026-10-26-earn-launch-copy.md §1）: prize / win / free を使わない。お金の筋は「KUMODeck が広告の枠の代金を払う」
 *   - 「見込み（Estimated）」と書く: 山分けの額は週の終わりまで下がりうる（企画書 §2-2）
 *   - 受け取りは Amazon のギフトカード（founder 10/26）。PayPal・withdraw（引き出す）とは書かない = まだ作っていない道を約束しない
 *   - 疑わしいと決めた理由は書かない（試して守りを探れない）。数だけ
 *   - 会社名・個人名を出さない（{product} と問い合わせ先のメールだけ）
 *   - 日の区切りは UTC（サーバーの決まり）
 *   - 受け取りの国（pe.country.*・2026-10-26 earn-build-2）: カードはその国の Amazon でしか使えない = 選ばないと送れない。未選択の時の文は指示書の
 *     「Choose your Amazon country to receive gift cards」そのまま
 */
export const enPlayEarn = {
  'pe.nav': 'Play earnings',
  'pe.title': 'Earnings',
  'pe.thisWeek': 'This week · {range} (UTC)',
  'pe.paused': 'Counting is paused right now. Plays that were already counted are still paid.',
  'pe.pool': 'Weekly {product} ad budget',
  'pe.poolUsed': '{amount} paid out so far (estimate)',
  'pe.rate': 'Your rate this week',
  'pe.rateValue': '{rate} / play',
  'pe.rateFull': 'Full rate while total plays stay under {plays}. If more people play, the {pool} is split by plays.',
  'pe.rateSplit': 'More people played than {pool} covers at {full} a play, so it is split by plays. It can still change until the week ends.',
  'pe.estimate': 'Estimated this week',
  'pe.estimateMath': '{counted} − {deducted} = {net} × {rate}',
  'pe.estimateMathPlain': '{net} × {rate}',
  'pe.estimateMathReview': '{counted} − {deducted} − {review} under review = {net} × {rate}',
  'pe.estimateCapped': 'Up to {cap} per creator per week.',
  'pe.counted': 'Counted plays',
  'pe.countedHint': 'One per person, per game, per day, on pages that showed a {product} ad',
  'pe.notCounted': 'Not counted',
  'pe.notCountedHint': 'Your own plays, repeats after the first play per person per game per day, and bots',
  'pe.deducted': 'Deducted',
  'pe.deductedHint': 'Each suspicious play subtracts one good play',
  'pe.byGame': 'By game',
  'pe.col.game': 'Game',
  'pe.col.plays': 'Plays',
  'pe.col.est': 'Est.',
  'pe.col.status': 'Status',
  'pe.status.ok': 'OK',
  'pe.status.review': 'Under review',
  'pe.reviewNote': 'A game under review earns nothing this week while we check its plays.',
  'pe.noGames': 'No counted plays this week yet.',
  'pe.onHold': 'On hold ({days}-day check)',
  'pe.onHoldHint': 'This week moves here when it closes on {day} (UTC).',
  'pe.ready': 'Being sent',
  'pe.readyHint': 'The check is over. It is on its way as an Amazon gift card.',
  'pe.sent': 'Sent so far',
  'pe.payLine': 'Paid as an Amazon gift card to your account email ({email}) after a {days}-day check.',
  'pe.country.label': 'Gift card country',
  'pe.country.choose': 'Choose your Amazon country to receive gift cards',
  'pe.country.placeholder': 'Choose a country',
  'pe.country.hint': 'Your gift card is bought on {store} and can only be used there.',
  'pe.country.saving': 'Saving…',
  'pe.country.saved': 'Saved',
  'pe.weeks': 'Past weeks',
  'pe.col.week': 'Week',
  'pe.col.amount': 'Amount',
  'pe.week.held': 'On hold until {day}',
  'pe.week.ready': 'Being sent',
  'pe.week.sent': 'Sent {day}',
  'pe.week.voided': 'Not paid',
  'pe.empty.title': 'Waiting for the first play',
  'pe.empty.body': 'Plays count from the day your game is published: one per person, per game, per day, on games that show {product} ads.',
  'pe.foot': "Ads shown in your game are {product}'s own ads. {product} pays you for the ad space. Questions: {email}",
  'pe.off.title': 'Not available yet',
  'pe.off.body': 'Earning from plays is not open on this account yet.',
  // 広告のカード（概要の画面・components/DisplayAdsCard.tsx）の試しの作り手の版（2026-10-27 console/earn-ads-card）。
  // 文は告知の文（docs/growth/campaign/2026-10-26-earn-launch-copy.md §3 /early・§4 メール）の言い方のまま = 新しい約束を足さない。
  // 「player」はダッシュボードの決まり（wording.test.ts）で使わない = 告知の "per player" は稼ぎの画面（pe.countedHint）と同じ "per person" にした。
  // 額・日数は GET /v1/money/earn の値（{rate} {pool} {cap} {days}）= サーバーの設定が変わっても文がずれない。「業界水準以上」「prize / free」は書かない
  'pe.card.rate': '{n}¢',
  'pe.card.body': 'Turn on ads and your game earns {rate} a play, from the first play. The ads are {product}’s own, and {product} adds them to your public page for you, with no code.',
  'pe.card.on': 'Your game earns {rate} a play. Watch your week in {link}.',
  'pe.card.play': 'One play per person, per game, per day. Bot plays and your own plays don’t count.',
  'pe.card.pot': 'This week’s pot is {pool}. If it runs out, it’s split by plays. Up to {cap} per creator a week.',
  'pe.card.paid': 'After the week ends, we check for {days} days, then send your earnings as an Amazon gift card to your account email.',
  'pe.card.more': 'Details in {link}.',
  'pe.card.amazon': 'Amazon is not a sponsor of this.',
  'pe.card.houseNote': 'Right now, the only ads in your app are short promos for {product}. Plays count on pages that showed a {product} ad. You choose where they can show.'
};
